import api from "@/api";
import router from "@/router";
import tokenManager from "@/tokenManager";
import {
  clearInactivityTimer,
  forceLoginRedirect,
  resetInactivityTimer,
  resetTerminationState,
} from "@/sessionManager";
import {
  authorizedTeamsForAbility,
  canPerformActionOnEntity,
  emptyAuthorizationState,
  getCommonTeamsWithPermission,
  getTeamsWithPermission,
  hasGlobalAuthorization,
  hasTeamAuthorization,
  loadPersistedAuthorization,
  normalizeAuthorization,
  persistAuthorization,
} from "@/helpers/teamAuthorization";

let currentTeamSwitchSequence = 0;

export default {
  state: {
    isLoggedIn: false,
    accessToken: "",

    loggedUser: JSON.parse(localStorage.getItem("user")) || {},
    teamAuthorization: loadPersistedAuthorization(),
  },
  getters: {
    accessToken: (state) => state.accessToken,
    loggedUser: (state) => state.loggedUser,
    isLoggedIn: (state) => state.isLoggedIn,
    teamAuthorization: (state) => state.teamAuthorization,
    authorizationLoaded: (state) => state.teamAuthorization.loaded,
    currentTeamId: (state) => state.teamAuthorization.currentTeamId,
    authorizationMemberships: (state) => Object.values(state.teamAuthorization.membershipsByTeamId),
    isSuperAdmin: (state) => {
      // Check user object field first (old API compatibility)
      const user = state.loggedUser;
      const userFlag = user?.is_super_admin === true || user?.is_super_admin === 1 || user?.is_super_admin === "1" || user?.is_super_admin === "true";
      const authorizationFlag = state.teamAuthorization?.super_admin === true;
      const adminRoles = Object.values(state.teamAuthorization?.membershipsByTeamId || {})
        .flatMap(membership => membership.roles || [])
        .filter(role => role.id === 96);
      const isSuperAdmin = userFlag || authorizationFlag || adminRoles.length > 0; 
      return isSuperAdmin;
    },
    hasGlobalPermission: (state, getters) => (ability) => hasGlobalAuthorization(
      state.teamAuthorization,
      ability,
      { isSuperAdmin: getters.isSuperAdmin }
    ),
    hasTeamPermission: (state, getters) => (ability, teamId) => hasTeamAuthorization(
      state.teamAuthorization,
      ability,
      teamId,
      { isSuperAdmin: getters.isSuperAdmin }
    ),
    authorizedTeamIds: (state, getters) => (ability) => authorizedTeamsForAbility(
      state.teamAuthorization,
      ability,
      { isSuperAdmin: getters.isSuperAdmin }
    ),
    isMemberOf: (state) => (teamId) =>
      Boolean(state.teamAuthorization.membershipsByTeamId[Number(teamId)]),
    membershipFor: (state) => (teamId) =>
      state.teamAuthorization.membershipsByTeamId[Number(teamId)] ?? null,
    
    // NEW: Team-based permission model
    /**
     * Get all team IDs where logged user has a specific permission.
     * @param {Ability} ability
     * @returns {TeamId[]}
     */
    getTeamsWithPermission: (state) => (ability) =>
      getTeamsWithPermission(state.teamAuthorization, ability),
    
    /**
     * Get common team IDs between logged user and target entity.
     * @param {Ability} ability
     * @param {TeamId[]} targetTeams - Team IDs from target entity
     * @returns {TeamId[]}
     */
    getCommonTeamsWithPermission: (state) => (ability, targetTeams) =>
      getCommonTeamsWithPermission(state.teamAuthorization, ability, targetTeams),
    
    /**
     * Check if logged user can perform an action on a specific entity.
     * Requires: logged user has permission in at least one team that entity belongs to.
     * @param {Ability} ability
     * @param {TeamId[]} targetTeams - Team IDs from target entity
     * @returns {boolean}
     */
    canPerformActionOnEntity: (state, getters) => (ability, targetTeams) => {
      if (getters.isSuperAdmin) {
        return true;
      }

      return canPerformActionOnEntity(state.teamAuthorization, ability, targetTeams);
    },

  },
  mutations: {
    setAccessToken: (state, data) => {
      state.accessToken = data;
    },
    setLoggedUser: (state, data) => {
      state.loggedUser = data; 
    },
    setIsLoggedIn: (state, data) => {
      state.isLoggedIn = data;
    },
    replaceAuthorization: (state, data) => {
      const payload = data?.payload ?? data;
      state.teamAuthorization = normalizeAuthorization(payload);
      persistAuthorization(state.teamAuthorization); 
    },
    clearAuthorization: (state) => {
      state.teamAuthorization = emptyAuthorizationState();
      persistAuthorization(state.teamAuthorization);
    },

  },
  actions: { 
    login: async ({ commit }, { email, password }) => {
      try {
        const response = await api({ requiresAuth: false }).post("/login", {
          email,
          password,
        });

        const { message } = response.data;

        commit("setToast", {
          display: true,
          type: "success",
          message: message,
        });

        return true;

      } catch (err) {
        const errorMessage = err.response?.data?.message || "An error occurred during login.";

        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        });
 

        return false;
      }
    },

    sendAuthCode: async ({ commit }, { email, code }) => {
      try {
        const response = await api({ requiresAuth: false }).post("/authenticate-code", {
          email,
          code
        });

        const { access_token, user, authorization } = response.data; 
        const successMessage = response.data?.message || "Authentication successful.";
        commit("setAccessToken", access_token);
        
        // SECURITY: Use tokenManager for secure token storage
        // This handles both httpOnly cookies and localStorage with TTL management
        tokenManager.storeToken(access_token);
        
        sessionStorage.setItem("needs_reset", user.needs_reset);
        commit("setLoggedUser", user);
        if (authorization) {
          commit("replaceAuthorization", {
            payload: authorization,
            isSuperAdmin: user?.is_super_admin === true,
          });
        }
        commit("setIsLoggedIn", true);
        resetTerminationState();
        resetInactivityTimer({
          timeoutMs: 60 * 60 * 1000,
        });
 
        localStorage.setItem("user", JSON.stringify(user));

        const validLocales = ['en', 'de', 'fr', 'it'];
        const normalizeLocaleCode = (value, fallback = 'de') => {
          if (typeof value === 'string') {
            return validLocales.includes(value) ? value : fallback;
          }

          if (value && typeof value === 'object') {
            const candidate = value.code || value.language || value.name;
            if (typeof candidate === 'string' && validLocales.includes(candidate)) {
              return candidate;
            }
          }

          return fallback;
        };

        const localeCode = normalizeLocaleCode(
          user?.language ?? user?.settings?.locale,
          'de'
        );

        window.localStorage.setItem("language", localeCode);
        commit("setLanguage", localeCode);

        commit("setToast", {
          display: true,
          type: "success",
          message: successMessage,
        });

        if (user.needs_reset)
          router.push({
            name: "AccountDetails", params: {
              entity: "profile",
              id: user.id,
            },
          });
        else {
          const redirect = router.currentRoute?.query?.redirect;
          const hasSafeRedirect = redirect
            && typeof redirect === "string"
            && redirect.startsWith("/")
            && !redirect.startsWith("//");

          if (hasSafeRedirect) {
            router.push(redirect);
          } else {
            router.push({ name: "Dashboard" });
          }
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Unable to authenticate.";

        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        });
 
      }
    },

    resendAuthCode: async ({ commit }, { email }) => {
      try {
        const response = await api({ requiresAuth: false }).post("/resend-code", {
          email,
        });

        const successMessage = response.data?.message || "Verification code resent successfully.";
        commit("setToast", {
          display: true,
          type: "success",
          message: successMessage,
        });
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Unable to resend verification code.";
        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        }); 
      }
    },

    logout: async ({ commit }) => {
      try {
        // SECURITY: Use tokenManager to safely retrieve token for logout
        const token = tokenManager.getToken() || localStorage.getItem("access_token");
        
        if (token || tokenManager.isUsingHttpOnlyCookies()) {
          const logoutUrl = token ? `/logout?${token}` : "/logout";
          await api({ requiresAuth: true }).get(logoutUrl, {
            skipAuthTermination: true,
          });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Unable to log out.";
        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        });
      } finally {
        clearInactivityTimer();
        forceLoginRedirect({ store: { commit } });
      }
    },

    // getLoggedUserData: async ({ commit }, userId) => {
    //   try {
    //     const response = await api({ requiresAuth: true }).get(
    //       `/v1/users/${userId}`
    //     );
    //     const { status } = response;
    //     const { data } = response.data;
    //     if (status === 200) {
    //       commit("setLoggedUser", data);
    //       commit("setIsLoggedIn", true);
    //       localStorage.setItem("user", JSON.stringify(data));
    //       router.push({ name: "Dashboard" });
    //     }
    //   } catch (err) {
    //     console.error(err);
    //   }
    // },
    updateLoggedUser: async ({ commit }, userId) => {
      try {
        const response = await api({ requiresAuth: true }).get(
          `/v1/users/${userId}`
        );
        const { data } = response.data;
        commit("setLoggedUser", data);
        if (response.data.authorization) {
          commit("replaceAuthorization", {
            payload: response.data.authorization,
            isSuperAdmin: data?.is_super_admin === true,
          });
        }
        localStorage.setItem("user", JSON.stringify(data));
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Unable to refresh user data.";
        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        }); 
      }
    },

    replaceAuthorization: ({ commit, state }, payload) => {
      commit("replaceAuthorization", {
        payload,
        isSuperAdmin: state.loggedUser?.is_super_admin === true,
      });
    },

    switchCurrentTeam: async ({ commit, state }, teamId) => {
      const normalizedTeamId = Number(teamId);
      if (!Number.isInteger(normalizedTeamId) || normalizedTeamId <= 0) {
        throw new TypeError("A valid team ID is required.");
      }
      if (!state.teamAuthorization.membershipsByTeamId[normalizedTeamId]) {
        throw new Error("The selected team is not one of the user's memberships.");
      }

      const requestSequence = ++currentTeamSwitchSequence;
      const response = await api({ requiresAuth: true }).patch("/v1/profile/current-team", {
        team_id: normalizedTeamId,
      }, {
        skipAuthorizationIngestion: true,
      });

      if (requestSequence !== currentTeamSwitchSequence) return false;

      commit("replaceAuthorization", {
        payload: response.data.authorization,
        isSuperAdmin: state.loggedUser?.is_super_admin === true,
      });
      return true;
    },


    updatePassword: async ({ commit }, { current_password, password, password_confirmation }) => {
      try {
        const response = await api({ requiresAuth: true }).patch(`/v1/profile-password`, {
          current_password,
          password,
          password_confirmation
        });
        const { data } = response.data;
        const successMessage = response.data?.message || "Password updated successfully.";

        commit("setLoggedUser", data);
        commit("setToast", {
          display: true,
          type: "success",
          message: successMessage,
        });
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Unable to update password.";

        commit("setToast", {
          display: true,
          type: "error",
          message: errorMessage,
        });
        throw err;
      }
    }
  },
};
